import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import RenderNode, { CategoryData, TreeNode } from "./RenderNode";
import VersionModal from "./VersionModal";

interface AccordionTableProps {
  data: Record<string, CategoryData>;
  selectedPaths: string[];
  onSelectionChange: (selectedPaths: string[]) => void;
  emptyMessage?: string;
  // all: 割り当て可能な全カテゴリを表示(作成・編集ページ)
  // group: 対象グループに割り当て済みのカテゴリを表示(一覧ページ)
  mode?: "all" | "group";
}

/**
 * スラッシュ区切りのカテゴリパスからツリー構造を構築
 */
const buildTree = (categoryMap: Record<string, CategoryData>): TreeNode[] => {
  const root: TreeNode = { name: "", path: "", level: 0, children: [] };

  Object.entries(categoryMap).forEach(([path, data]) => {
    const parts = path.split("/").filter(Boolean);
    let currentNode = root;
    let currentPath = "";

    parts.forEach((part, index) => {
      currentPath += `/${part}`;
      const level = index + 1;

      let childNode = currentNode.children.find(child => child.name === part);
      if (!childNode) {
        childNode = {
          name: part,
          path: currentPath + "/",
          level,
          children: [],
          isExpanded: false,
        };
        currentNode.children.push(childNode);
      }

      // 最後の階層の場合、データ(所属グループとバージョン)を付与
      if (index === parts.length - 1) {
        childNode.data = data;
      }

      currentNode = childNode;
    });
  });

  return root.children;
};

/**
 * ツリー内の全ノードをフラットな配列で取得
 */
const getAllNodes = (nodes: TreeNode[]): TreeNode[] => {
  return nodes.reduce<TreeNode[]>((acc, node) => {
    return [
      ...acc,
      node,
      ...getAllNodes(node.children),
    ];
  }, []);
};

/**
 * 指定したノードの子孫ノードを全て取得
 */
const getNodeDescendants = (node: TreeNode): TreeNode[] => {
  return node.children.reduce<TreeNode[]>((acc, child) => {
    return [
      ...acc,
      child,
      ...getNodeDescendants(child),
    ];
  }, []);
};

/**
 * 多階層アコーディオンテーブルコンポーネント
 */
const AccordionTable: React.FC<AccordionTableProps> = (
  {
    data,
    selectedPaths,
    onSelectionChange,
    emptyMessage = "割り当てられているカテゴリはありません",
    mode = "all",
  },
) => {
  const tree = useMemo(() => buildTree(data), [data]);
  const allNodes = useMemo(() => getAllNodes(tree), [tree]);

  // mode='group'の場合は初期状態で全展開
  const initialExpandedNodes = useMemo(
    () => mode === "group" ? new Set(allNodes.map(node => node.path)) : new Set<string>(),
    [mode, allNodes],
  );

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(initialExpandedNodes);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [versions, setVersions] = useState<string[]>([]);

  // テーブルを内スクロールにするため、画面上端からテーブルまでの長さをgetBoundingClientRectで取得する
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number | null>(null);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setTop(rect.top);
    }
  }, []);

  // 指定したパスの子孫ノードを全て取得
  const getDescendants = (targetPath: string): string[] => {
    const targetNode = allNodes.find(node => node.path === targetPath);
    if (!targetNode) return [];

    return getNodeDescendants(targetNode).map(descendant => descendant.path);
  };

  // 指定したパスの祖先ノードを全て取得 ("/a/b/c/" -> ["/a/", "/a/b/"])
  const getAncestors = useCallback((targetPath: string): string[] => {
    const pathParts = targetPath.split("/").filter(Boolean);
    return pathParts.slice(0, -1).map((_, i) => "/" + pathParts.slice(0, i + 1).join("/") + "/");
  }, []);

  // 指定したパスの直接の子ノードを取得
  const getDirectChildren = (targetPath: string): string[] => {
    const targetNode = allNodes.find(node => node.path === targetPath);
    if (!targetNode) return [];

    return targetNode.children.map(child => child.path);
  };

  const toggleSelect = (path: string) => {
    const newSelected = new Set(selectedNodes);
    const isCurrentlySelected = newSelected.has(path);

    if (isCurrentlySelected) {
      // オフにする場合：自身と全ての子孫を非選択にする
      newSelected.delete(path);
      const descendants = getDescendants(path);
      descendants.forEach(descendant => newSelected.delete(descendant));

      // 祖先の選択状態を確認して更新 (子が全てオフなら親もオフにする)
      getAncestors(path).toReversed().forEach(ancestor => { // 下層から上層へ確認するためのtoReversed()
        const children = getDirectChildren(ancestor);
        const allChildrenUnselected = children.every(child => !newSelected.has(child));
        if (allChildrenUnselected) {
          newSelected.delete(ancestor);
        }
      });
    } else {
      // オンにする場合：自身と全ての子孫を選択にする
      newSelected.add(path);
      const descendants = getDescendants(path);
      descendants.forEach(descendant => newSelected.add(descendant));

      // 祖先の選択状態を確認して更新（一つでも子がオンなら親もオンにする）
      getAncestors(path).toReversed().forEach(ancestor => {
        const children = getDirectChildren(ancestor);
        const anyChildSelected = children.some(child => newSelected.has(child));
        if (anyChildSelected && children.length > 0) {
          newSelected.add(ancestor);
        }
      });
    }

    setSelectedNodes(newSelected);

    // 親コンポーネントに選択状態の変更通知
    onSelectionChange(Array.from(newSelected));
  };

  // 親コンポーネントのselectedPaths状態を反映
  useEffect(() => {
    const ancestors = selectedPaths.map(path => getAncestors(path)).flat();
    setSelectedNodes(new Set([...ancestors, ...selectedPaths]));
  }, [selectedPaths, getAncestors]);

  const isSelected = (path: string) => selectedNodes.has(path);

  const toggleExpand = (path: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(path)) {
      // 閉じる場合：自身と全ての子孫を非展開にする
      newExpanded.delete(path);
      const descendants = getDescendants(path);
      descendants.forEach(descendant => newExpanded.delete(descendant));
    } else {
      // 開く場合：自身と全ての子孫を展開にする
      newExpanded.add(path);
      const descendants = getDescendants(path);
      descendants.forEach(descendant => newExpanded.add(descendant));
    }
    setExpandedNodes(newExpanded);
  };

  const isExpanded = (path: string) => expandedNodes.has(path);

  const handleVersionClick = (versions: string[]) => {
    setVersions(versions);
    setModalOpen(true);
  };

  return (
    <>
      <div className="w-full">
        <div ref={ref} className="w-full border border-[#dee2e6] rounded-[5px] overflow-hidden">
          {/* テーブルヘッダー */}
          <div className="flex bg-[#293d72] font-bold">
            <div
              className="flex items-center px-4 py-2 border-r border-[#dee2e6]"
              style={{ flex: 1 }}
            >
              <span className="text-white text-sm">
                カテゴリ名
              </span>
            </div>
            <div
              className="flex items-center justify-center px-4 py-2 border-r border-[#dee2e6]"
              style={{ width: "264px" }}
            >
              <span className="text-white text-sm">
                {mode === "all" ? "現在" : "他に"}割り当てられているグループ
              </span>
            </div>
            <div
              className="flex items-center justify-center px-4 py-2"
              style={{ width: "144px" }}
            >
              <span className="text-white text-sm">
                バージョン情報
              </span>
            </div>
          </div>

          {/* テーブルボディ */}
          {Object.keys(data).length === 0 && (
            <div className="p-4 text-center text-gray-500">
              {emptyMessage}
            </div>
          )}
          <ScrollArea className="h-full">
            <div
              style={{ maxHeight: mode === "all" ? `calc(100vh - ${(top ?? 0) + 80}px)` : "auto" }}
            >
              {tree.map(node => (
                <RenderNode
                  key={node.path}
                  node={node}
                  isExpanded={isExpanded}
                  isSelected={isSelected}
                  toggleExpand={toggleExpand}
                  toggleSelect={toggleSelect}
                  onVersionClick={handleVersionClick}
                  mode={mode}
                />
              ))}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      </div>

      {/* バージョン情報モーダル */}
      <VersionModal
        modalOpen={modalOpen}
        versions={versions}
        setModalOpen={setModalOpen}
      />
    </>
  );
};

export default AccordionTable;
